import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Box,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Add,
  Remove,
  ShoppingCart,
  CreditCard,
  MoneyOff,
} from "@mui/icons-material";
import { useIntl } from "react-intl";
import { Price } from "../../redux/types/types";

interface CartItem {
  priceId: number;
  price: Price;
  quantity: number;
}

interface MultiPackagePaymentDialogProps {
  open: boolean;
  onClose: () => void;
  attendeeId: number;
  prices: Price[];
  onManualPayment: (cartItems: CartItem[]) => Promise<void>;
  onStripePayment: (cartItems: CartItem[]) => Promise<void>;
  loading: boolean;
  canDoManualPayment: boolean;
}

export const MultiPackagePaymentDialog: React.FC<
  MultiPackagePaymentDialogProps
> = ({
  open,
  onClose,
  attendeeId,
  prices,
  onManualPayment,
  onStripePayment,
  loading,
  canDoManualPayment,
}) => {
  const intl = useIntl();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  useEffect(() => {
    if (open) {
      // Reset cart when dialog opens
      setCartItems([]);
    }
  }, [open]);

  const addToCart = (price: Price) => {
    setCartItems((prev) => {
      const existingItem = prev.find((item) => item.priceId === price.id);
      if (existingItem) {
        return prev.map((item) =>
          item.priceId === price.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prev, { priceId: price.id, price, quantity: 1 }];
      }
    });
  };

  const updateQuantity = (priceId: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      setCartItems((prev) => prev.filter((item) => item.priceId !== priceId));
    } else {
      setCartItems((prev) =>
        prev.map((item) =>
          item.priceId === priceId ? { ...item, quantity: newQuantity } : item
        )
      );
    }
  };

  const removeFromCart = (priceId: number) => {
    setCartItems((prev) => prev.filter((item) => item.priceId !== priceId));
  };

  const getTotalAmount = () => {
    return cartItems.reduce(
      (total, item) => total + item.price.amount * item.quantity,
      0
    );
  };

  const getTotalClasses = () => {
    return cartItems.reduce(
      (total, item) => total + item.price.numberOfClasses * item.quantity,
      0
    );
  };

  const formatPrice = (amount: number) => {
    return (amount / 100).toFixed(2);
  };

  const handleManualPayment = async () => {
    if (cartItems.length === 0) return;
    await onManualPayment(cartItems);
  };

  const handleStripePayment = async () => {
    if (cartItems.length === 0) return;
    await onStripePayment(cartItems);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ShoppingCart />
          {intl.formatMessage({ id: "payment.multiPackage.title" })}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Available Packages */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              {intl.formatMessage({
                id: "payment.multiPackage.availablePackages",
              })}
            </Typography>
            <Grid container spacing={2}>
              {prices.map((price) => (
                <Grid item xs={12} key={price.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography variant="subtitle1">
                            {price.specialType ||
                              intl.formatMessage({ id: "payment.package" })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {price.numberOfClasses}{" "}
                            {intl.formatMessage({ id: "payment.classes" })} -
                            {formatPrice(price.amount)} CHF
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => addToCart(price)}
                          size="small"
                        >
                          {intl.formatMessage({
                            id: "payment.multiPackage.addToCart",
                          })}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Shopping Cart */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              {intl.formatMessage({ id: "payment.multiPackage.cart" })} (
              {cartItems.length})
            </Typography>

            {cartItems.length === 0 ? (
              <Alert severity="info">
                {intl.formatMessage({ id: "payment.multiPackage.emptyCart" })}
              </Alert>
            ) : (
              <Box>
                {cartItems.map((item) => (
                  <Card key={item.priceId} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box flex={1}>
                          <Typography variant="subtitle2">
                            {item.price.specialType ||
                              intl.formatMessage({ id: "payment.package" })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.price.numberOfClasses}{" "}
                            {intl.formatMessage({ id: "payment.classes" })} ×
                            {formatPrice(item.price.amount)} CHF
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              updateQuantity(item.priceId, item.quantity - 1)
                            }
                          >
                            <Remove />
                          </IconButton>
                          <TextField
                            size="small"
                            value={item.quantity}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0;
                              updateQuantity(item.priceId, value);
                            }}
                            type="number"
                            inputProps={{
                              min: 1,
                              style: { textAlign: "center", width: "60px" },
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() =>
                              updateQuantity(item.priceId, item.quantity + 1)
                            }
                          >
                            <Add />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeFromCart(item.priceId)}
                          >
                            <Remove />
                          </IconButton>
                        </Box>
                      </Box>
                      <Typography variant="body2" align="right" sx={{ mt: 1 }}>
                        {intl.formatMessage({ id: "payment.subtotal" })}:{" "}
                        {formatPrice(item.price.amount * item.quantity)} CHF
                      </Typography>
                    </CardContent>
                  </Card>
                ))}

                <Divider sx={{ my: 2 }} />

                <Box>
                  <Typography variant="h6" align="right">
                    {intl.formatMessage({ id: "payment.total" })}:{" "}
                    {formatPrice(getTotalAmount())} CHF
                  </Typography>
                  <Typography
                    variant="body2"
                    align="right"
                    color="text.secondary"
                  >
                    {intl.formatMessage({ id: "payment.totalClasses" })}:{" "}
                    {getTotalClasses()}
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {intl.formatMessage({ id: "common.cancel" })}
        </Button>
        <Box display="flex" gap={1}>
          {/* Manual payment is only available to admins and instructors */}
          {canDoManualPayment && (
            <Button
              variant="outlined"
              startIcon={
                loading ? <CircularProgress size={16} /> : <MoneyOff />
              }
              onClick={handleManualPayment}
              disabled={cartItems.length === 0 || loading}
              color="secondary"
            >
              {intl.formatMessage({ id: "payment.manual" })}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={
              loading ? <CircularProgress size={16} /> : <CreditCard />
            }
            onClick={handleStripePayment}
            disabled={cartItems.length === 0 || loading}
          >
            {intl.formatMessage({ id: "payment.stripe" })}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
